import React, { FC, useCallback, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import jwt_decode from 'jwt-decode';

import { StaffService } from 'src/services/UserServices';

import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import USERS, { getUserDataWithUsername } from '../../../common/data/userDummyData';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';
import { getStorage, setStorage } from 'src/utils/utils';
import logoimg from '../../../assets/logo.png';
import showNotification from 'src/components/extras/showNotification';
import Auth2FA from './Auth2FA';
import { useDispatch } from 'react-redux';
import { setRole } from 'src/redux/slices/user';

const LoginHeader = ({ isNewUser }) => {
	return (
		<>
			<div className='text-center h1 fw-bold mt-5'>Welcome</div>
			<div className='text-center h4 text-muted mb-5'>JsPay {'{BackOffice}'}</div>
		</>
	);
};
LoginHeader.defaultProps = {
	isNewUser: false,
};

const Login = ({ isSignUp }) => {
	const dispatch = useDispatch();
	const { i18n, t } = useTranslation();
	const { setUser } = useContext(AuthContext);
	const [singUpStatus, setSingUpStatus] = useState(!!isSignUp);
	const { darkModeStatus } = useDarkMode();
	const navigate = useNavigate();
	const handleOnClick = useCallback(() => navigate('/'), [navigate]);
	const [show2Fa, setShow2Fa] = useState(false);
	const [qrString, setQrString] = useState(null);
	const [isOpenInput2fa, setIsOpenInput2fa] = useState('');
	// const [username, setUsername] = useState();
	// const [password, setPassword] = useState();
	const [token2fa, setToken2fa] = useState();
	const [status2Fa, setStatus2Fa] = useState('');

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginUsername: '',
			loginPassword: '',
			token: '',
		},
		validate: (values) => {
			const errors = {};

			if (!values.loginUsername) {
				errors.loginUsername = 'Required';
			}

			if (!values.loginPassword) {
				errors.loginPassword = 'Required';
			}

			// if (!values.token) {
			// 	errors.token = 'Required';
			// }

			return errors;
		},
		validateOnChange: false,
		onSubmit: (values) => {
			let requestData = {
				username: values?.loginUsername,
				password: values?.loginPassword,
				token: token2fa,
			};

			if (show2Fa) requestData = { ...requestData };

			StaffService.signIn(requestData)
				.then(({ data }) => {
					if (data?.status) {
						setQrString(data?.twoFa);
						setShow2Fa(true);
						return;
					} else {
						if (data?.statusCode === 4001) {
							return show2Fa
								? showNotification(
										<span className='d-flex align-items-center'></span>,
										`${t('txt_tryagain')}`,
									)
								: setShow2Fa(true);
						}
					}

					let { username, userid, exp, role } = jwt_decode(data?.access_token);
					setUser(username);
					dispatch(setRole(role));

					setStorage('roles', role);
					setStorage('i18nextLng', 'th');
					setStorage('token', data?.access_token);
					setStorage('userId', userid);
					setStorage('username', username);
					setStorage('expired', exp);
					setStorage(
						'role',
						JSON.stringify({
							name_en: data?.permission?.name_en,
							name_th: data?.permission?.name_th,
						}),
					);

					handleOnClick();
				})
				.catch((err) => {
					setIsOpenInput2fa(
						err?.response?.data?.message === 'required-2fa-token' ? true : '',
					);
					formik.setFieldError('loginUsername', 'No such user found in the system.');
					formik.setFieldError('loginPassword', `${err?.response?.data?.message}`);
					formik.setFieldError('token2fa', `${err?.response?.data?.message}`);
				});
		},
	});

	const render2FA = () => {
		return (
			show2Fa && (
				<Auth2FA
					handleSunmit2FA={formik.handleSubmit}
					setToken={setToken2fa}
					token={token2fa}
					QR={qrString}
				/>
			)
		);
	};

	const [isLoading, setIsLoading] = useState(false);

	return (
		<>
			{!show2Fa && (
				<PageWrapper
					isProtected={false}
					title={'Login'}
					className={classNames({ 'bg-dark': !singUpStatus, 'bg-light': singUpStatus })}>
					<Page className='p-0'>
						<div className='row h-100 align-items-center justify-content-center'>
							<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
								<Card className='shadow-3d-dark' data-tour='login-page'>
									<CardBody>
										<div className='text-center my-5'>
											<Link
												to='#'
												className={classNames(
													'text-decoration-none  fw-bold display-2',
													{
														'text-dark': !darkModeStatus,
														'text-light': darkModeStatus,
													},
												)}
												aria-label='Facit'>
												<img src={logoimg} width={200} />
											</Link>
										</div>
										<div
											className={classNames('rounded-3', {
												'bg-l10-dark': !darkModeStatus,
												'bg-dark': darkModeStatus,
											})}></div>

										<LoginHeader />

										<form className='row g-4'>
											<div className='col-12'>
												<FormGroup
													id='loginUsername'
													isFloating
													label={t('txt_username')}>
													<Input
														autoComplete='username'
														value={formik.values.loginUsername}
														isTouched={formik.touched.loginUsername}
														invalidFeedback={
															formik.errors.loginUsername
														}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														onFocus={() => {
															formik.setErrors({});
														}}
													/>
												</FormGroup>

												<FormGroup
													id='loginPassword'
													isFloating
													label={t('txt_password')}
													className={'mt-2'}>
													<Input
														type='password'
														autoComplete='current-password'
														value={formik.values.loginPassword}
														isTouched={formik.touched.loginPassword}
														invalidFeedback={
															formik.errors.loginPassword
														}
														validFeedback='Looks good!'
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
													/>
												</FormGroup>

												{isOpenInput2fa ? (
													<FormGroup label='2FA' className={'mt-2'}>
														<Input
															type='text'
															value={token2fa}
															onChange={(e) =>
																setToken2fa(e.target.value)
															}
														/>
													</FormGroup>
												) : (
													''
												)}
											</div>
											<div className='col-12'>
												<Button
													color='warning'
													className='w-100 py-3'
													onClick={formik.handleSubmit}>
													{t('txt_login')}
												</Button>
											</div>
										</form>
									</CardBody>
								</Card>
							</div>
						</div>
					</Page>
				</PageWrapper>
			)}
			{render2FA()}
		</>
	);
};
Login.propTypes = {
	isSignUp: PropTypes.bool,
};
Login.defaultProps = {
	isSignUp: false,
};

export default Login;
